import { ref } from "vue";
import type { Ref } from "vue";
import type {
  Reservation,
  ReservationRequest,
  ReservationDeclineReason,
} from "@lemmonhq/types";
import type { CreateReservationData } from "@app/server/routes/shop/routes";
import { useShop } from "@app/vue/providers/ShopConnection";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useQueryClient } from "@tanstack/vue-query";
import { invalidateReservations } from "../data/invalidation";

export function useSheetState(params: {
  rejectMutate: (input: {
    request_id: string;
    status: "rejected" | "cancelled_by_host" | "cancelled_by_user";
    reason?: ReservationDeclineReason;
    message?: string;
  }) => Promise<unknown>;
  reservationPages: Ref<Reservation[]>;
  onAfterMutation?: () => void;
}) {
  const { rejectMutate, reservationPages, onAfterMutation } = params;
  const shop = useShop();
  const trpc = useTrpc();
  const queryClient = useQueryClient();

  const selectedRequest = ref<ReservationRequest | null>(null);
  const selectedReservation = ref<Reservation | null>(null);

  const showRequestDetailsSheet = ref(false);
  const showAcceptSheet = ref(false);
  const showRejectSheet = ref(false);
  const showReservationDetailsSheet = ref(false);
  const showCreateReservationForm = ref(false);
  const showScheduledClosures = ref(false);

  const linkedReservation = () =>
    selectedRequest.value?.reservation_id
      ? (reservationPages.value.find(
          (r) => r.id === selectedRequest.value?.reservation_id,
        ) ?? null)
      : null;

  const handleRequestClick = (request: ReservationRequest) => {
    selectedRequest.value = request;
    showRequestDetailsSheet.value = true;
  };

  const handleRequestAccept = (request: ReservationRequest) => {
    selectedRequest.value = request;
    showAcceptSheet.value = true;
  };

  const handleRequestReject = (request: ReservationRequest) => {
    selectedRequest.value = request;
    showRejectSheet.value = true;
  };

  const handleReservationClick = (reservation: Reservation) => {
    selectedReservation.value = reservation;
    showReservationDetailsSheet.value = true;
  };

  // Called from Details sheet when user clicks Accept — close Details, open Accept sheet
  const handleAcceptIntent = () => {
    showRequestDetailsSheet.value = false;
    showAcceptSheet.value = true;
  };

  // Called from Details sheet when user clicks Decline — close Details, open Reject sheet
  const handleRejectIntent = () => {
    showRequestDetailsSheet.value = false;
    showRejectSheet.value = true;
  };

  const handleCancelIntent = async () => {
    if (!selectedRequest.value) return;
    try {
      await rejectMutate({
        request_id: selectedRequest.value.id,
        status: "cancelled_by_host",
      });
      showRequestDetailsSheet.value = false;
      // onSuccess callback on the mutation composable handles refreshTabCounts
    } catch (e) {
      console.error("Failed to cancel request:", e);
    }
  };

  const handleActionComplete = (
    _updatedRequest?: ReservationRequest,
    _newReservation?: Reservation,
  ) => {
    showRequestDetailsSheet.value = false;
    showAcceptSheet.value = false;
    showRejectSheet.value = false;
    onAfterMutation?.();
  };

  const handleReservationUpdate = (updatedReservation: Reservation) => {
    if (selectedReservation.value?.id === updatedReservation.id) {
      selectedReservation.value = updatedReservation;
    }
    void invalidateReservations(queryClient);
    onAfterMutation?.();
  };

  const handleIgnore = (_ignoredRequest: ReservationRequest) => {
    shop.refreshData();
  };

  const handleCreateReservation = async (formData: CreateReservationData) => {
    await trpc.reservations.create.mutate(formData);
    showCreateReservationForm.value = false;
    await invalidateReservations(queryClient);
    onAfterMutation?.();
    void shop.refreshData();
  };

  const closeAcceptSheet = () => {
    showAcceptSheet.value = false;
    setTimeout(() => {
      selectedRequest.value = null;
    }, 300);
  };

  return {
    selectedRequest,
    selectedReservation,
    showRequestDetailsSheet,
    showAcceptSheet,
    showRejectSheet,
    showReservationDetailsSheet,
    showCreateReservationForm,
    showScheduledClosures,
    linkedReservation,
    handleRequestClick,
    handleRequestAccept,
    handleRequestReject,
    handleReservationClick,
    handleAcceptIntent,
    handleRejectIntent,
    handleCancelIntent,
    handleActionComplete,
    handleReservationUpdate,
    handleIgnore,
    handleCreateReservation,
    closeAcceptSheet,
  };
}
